/* eslint-disable prettier/prettier */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import defaultImg from 'src/assets/images/no-user-picture.svg';
import { Rating } from '@material-ui/lab';
import { BaseDivider, BaseTypography } from 'src/components/common';
import { BaseChipsList } from 'src/components/common/BaseChip/BaseChipsList';
import { MediaSection } from './mediaViewer/MediaSection';
import { TabMenu } from "./tabMenu/TabMenu";
import { IGuestInfo } from '../../../api/base/Guest/types';
import { getReadableDate } from '../../../utils/helpers';
import { Avatar, Grid } from '@material-ui/core';
import 'src/components/common/styles/contact-guest.css';
import LinkedInIcon from 'src/components/common/svgIcons/LinkedInIcon';
import ExpertiseTreeView from 'src/features/landing/searchExpert/contactGuest/ExpertiseTreeView';

const useStyles = makeStyles((theme) => ({
	contentWrapper: {
		marginBottom: '1rem',
		maxWidth: '100%',
	},
	userData: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: '10px',
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: '1fr',
		},
	},
	avatar: {
		width: '100px',
		height: '100px',
	},
	rootInfo: {
		display: 'grid',
		gridTemplateColumns: '80px 345px 300px',
		gridGap: '46px',
		[theme.breakpoints.down('md')]: {
			gridTemplateColumns: '80px 200px 1fr',
		},
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: '1fr',
			gridGap: '15px',
		},
	},
	info__avatarSection: {
		display: 'flex',
		flexDirection: 'column',
	},
	info__avatar: {
		width: '120px',
		height: '120px',
		borderRadius: '50%',
		objectFit: 'fill'
	},
	info__rating: {
		marginTop: '8px',
	},
	info__fields: {
		display: 'grid',
		gridGap: '14px',
		wordBreak: 'break-all',
	},
	info__fieldLabel: {
		fontWeight: 500,
		fontSize: '12px',
		lineHeight: '140%',
		letterSpacing: '0.03em',
		color: '#949C9E',
	},

	info__companySection: {
		display: 'grid',
		gridAutoRows: 'max-content',
		gridGap: '8px',
		alignItems: 'baseline',
	},
	media: {
		margin: '20px 0',
	},
	link: {
		color: theme.palette.primary.contrastText,
		fontWeight: 400,
		fontSize: '14px',
		lineHeight: '18px',
		textDecoration: 'none',
	},
	hr: {
		width: 'calc(100% + 40px)',
		position: 'relative',
		marginTop: '20px',
		marginBottom: '10px',
		background: '#78c5f182',
		height: '1px',
		border: '0',
	},
	itemTitle: {
		padding: '12px 0 5px 0',
		fontWeight: '500'
	},
	userName: {
		fontWeight: 600,
		fontSize: '16px',
		lineHeight: '140%',
		color: theme.palette.primary.contrastText,
	},
	userLabel: {
		color: '#ababab'
	},
	field__label: {
		fontWeight: 500,
		fontSize: '12px',
		lineHeight: '140%',
		letterSpacing: '0.03em',
		color: '#949C9E',
	},
	field__value: {
		fontWeight: 500,
		fontSize: '14px',
	},
	field__value_country: {
		fontWeight: 500,
		fontSize: '14px',
		display: 'inline-block',
	},
	field__value_language: {
		fontWeight: 500,
		fontSize: '14px',
	},
	btn: {
		color: 'white',
		border: '1px solid transparent',
		boxShadow: 'unset',
		boxSizing: 'border-box',
		backgroundColor: '#0090E5',
		padding: '4px 10px',
		fontSize: '0.8125rem',
		height: '30px',
		textDecoration: 'none',
		fontWeight: '500',
		margin: '10px 5px 0 0',
		display: 'inline-block',
		'&:hover': {
			border: '1px solid rgba(17, 17, 17, 0.12)',
			backgroundColor: '#00A0FF',
			boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
		}
	},
	overTitle: {
		marginBottom: '6px',
	}, 
	guestContainer: {
		display: 'flex',
		'& .w-50': {
			width: '50%',
		}, 
		[theme.breakpoints.down('md')]: {
			display: 'block',
			'& .w-50': {
				width: '100%'
			}
		},
	},
}));


export type PContactOverview = {
	guest: IGuestInfo;
	isGuest?: boolean;
};

export const ContactOverview: React.FC<PContactOverview> = (props) => {
	const { guest, isGuest = false } = props;
	const classes = useStyles();

	const companyLogo =
		(guest?.is_company_logo_visible) ? guest?.guest_company_logo || guest?.manager_company_logo : '';
	const companyDescription = (guest?.is_company_description_visible) ? guest?.guest_company_description || guest?.manager_company_description : '';

	return (
		<div className={classes.contentWrapper}>
			<div className={classes.userData}>
				<div className="userImgAndPicture">
					<div className='image mr-32'>
						<Avatar
							variant="circle"
							className={classes.info__avatar}
							alt="User Avatar"
							src={guest?.avatar || defaultImg}
						/>
						<div className='linkedin-icon'>
							<a href={guest?.linkedin} target="_blank" rel="noopener noreferrer">
								<LinkedInIcon />
							</a>
						</div>
					</div>
					<Grid item md={10} xs={12} className='user-bio'>
						<div className='w-50'>
							<BaseTypography>
								<div className="user-label">Name </div>
								<div className="userValue mb-12-5">{guest?.first_name} {guest?.last_name}</div>
							</BaseTypography>
							<BaseTypography className={classes.field__value}>
								<div className="user-label">Title </div>
								<div className="userValue mb-12-5">{guest?.title}</div>
							</BaseTypography>
							{guest?.company_name && (
								<BaseTypography className={classes.field__value}>
									<div className="user-label">Company name </div>
									<div className="userValue mb-0">{guest?.company_name}</div>
								</BaseTypography>
							)
							}
						</div>
						<div className='w-50'>
							{guest?.gender && (
								<BaseTypography className={classes.field__value_country}>
									<div className="user-label">Gender </div>
									<div className="userValue mb-12-5">{guest?.gender}</div>
								</BaseTypography>
							)}
							{guest?.country && guest?.country.length > 0 && (
								<BaseTypography className={classes.field__value}>
									<div className="user-label">Country of residence </div>
									<div className="userValue mb-12-5">{guest?.country.map((ct) => ct.name).join(', ')}</div>
								</BaseTypography>
							)}
							{guest?.languages && guest?.languages.length > 0 && (
								<BaseTypography className={classes.field__value_language}>
									<div className="user-label">Languages </div>
									<div className="userValue mb-0">{guest?.languages.map((language) => language.name).join(', ')}</div>
								</BaseTypography>
							)}
						</div>
					</Grid>
				</div>
			</div>
			<BaseDivider />
			<div className="key-information">
				Key Information
			</div>
			<Grid item md={12} xs={12} className='user-bio'>
				{(guest?.is_sharepoint) && (isGuest) && (guest?.cba_lead || (guest?.last_date_trained && guest?.last_date_trained !== '0000-00-00') || guest?.media_license_expiry) &&
					<>
						<div className='w-50'>
							{guest?.cba_lead && (
								<BaseTypography style={{ marginTop: '24px' }} className={classes.field__value}>
									<div className="user-label">Media strategy:</div><span style={{color: '#0A0A0A'}}> {guest?.cba_lead}</span>
								</BaseTypography>
							)}
							{guest?.last_date_trained && guest?.last_date_trained !== '0000-00-00' && (
								<BaseTypography style={{ marginTop: '24px' }} className={`${classes.field__value} mt-25`}>
									<div className="user-label">Last date trained:</div><span style={{color: '#0A0A0A'}}> {getReadableDate(guest?.last_date_trained)}</span>
								</BaseTypography>
							)}
							{guest?.media_license_expiry && guest?.media_license_expiry !== '0000-00-00' && (
								<BaseTypography style={{ marginTop: '24px' }} className={`${classes.field__value} mt-25`}>
									<div className="user-label">Media license expiry:</div><span style={{color: '#0A0A0A'}}> {getReadableDate(guest?.media_license_expiry)}</span>
								</BaseTypography>
							)}
						</div>
					</>
				}

				<div className='w-100'>
					{guest?.rating !== 0 && (
						<>
							<div className="key-information-title mb-7" style={{ marginTop: '24px' }}>
								Rating
							</div>

							<Rating
								name="rating"
								size="medium"
								value={guest?.rating || 0}
								precision={1}
								readOnly
							/>
						</>
					)}
					{guest?.available && guest?.available.length > 0 && (
						<div>
							<div className="key-information-title mb-7" style={{ marginTop: '24px' }}>
								Available for appearances in
							</div>
							<BaseChipsList
								list={guest?.available.map((item) => item.name) || []}
							/>
						</div>
					)}
					{guest?.expertise && guest?.expertise.length > 0 && guest?.selectedExpertiseIds?.some((item) => item.hierarchy_id) && (
						<div>
							<div className="key-information-title mt-25 mb-7">
								Areas of expertise
							</div>
							{ /* <BaseChipsList
								list={guest?.expertise.map((item) => item.name) || []}
							/> */ }
							<ExpertiseTreeView
								expertiseTree={guest?.expertiseTree}
								selectedExpertiseIds={guest?.selectedExpertiseIds}
							/>
						</div>
					)}
				</div>
			</Grid>

			{guest?.description && (
				<>
					<div className="key-information-title mt-25 mb-7">Unique insights</div>
					<div style={{ fontWeight: 400, fontSize: "16px", lineHeight: "24px", wordWrap: "break-word", color: "#0A0A0A", }}>
						{guest?.description}
					</div>
				</>
			)}

			{(guest?.is_sharepoint) && (isGuest) && (guest?.training_certificate || guest?.onedrive_link) &&
				<>
					<BaseDivider />
					<div className="key-information">
						Training Information
					</div>
					{ /* guest?.training_certificate && (
						<>
							<div className="key-information-title mt-25 ">Training certificate</div>
							<a href={guest?.training_certificate} target='_blank'
								style={{
									fontWeight: 400,
									fontSize: "16px",
									lineHeight: "24px",
									color: '#4A9EDB',
									display: 'inline-block',
									textDecoration: 'none'
								}} rel="noreferrer">
								{guest?.training_certificate}
							</a>
						</>
					) */ }
					{ /* guest?.onedrive_link && (
						<>
							<div style={{ marginTop: '16px' }} className="key-information-title ">Training videos</div>
							<a href={guest?.onedrive_link} target='_blank'
								style={{
									fontWeight: 400,
									fontSize: "16px",
									lineHeight: "24px",
									color: '#4A9EDB',
									display: 'inline-block',
									textDecoration: 'none'
								}} rel="noreferrer">
								{guest?.onedrive_link}
							</a>
						</>
					) */ }
					<Grid item md={12}>
					  <TabMenu
						className={classes.media}
						resource={guest || []}
						Mediatrainingfeedback={guest?.api_response || []}
						// images={guest?.images || []}
						comeFrom="overview"
					  />
					</Grid>
						
				</>
			}
			{ /* guest?.background_briefings_story && (
				<>
					<div style={{ marginTop: '16px' }} className="key-information-title">Background briefing</div>
					<a href={guest?.background_briefings_story} target='_blank'
						style={{
							fontWeight: 400,
							fontSize: "16px",
							lineHeight: "24px",
							color: '#4A9EDB',
							display: 'inline-block',
							textDecoration: 'none'
						}} rel="noreferrer">
						{guest?.background_briefings_story}
					</a>
				</>
			) */ }

			{(companyLogo || companyDescription) &&
				<>
					<BaseDivider />
					<div className="key-information">
						Company Information
					</div>
					<div>{companyLogo && (
						<>
							<div className="key-information-title mt-25 mb-7">
								Company logo
							</div>
							<img height={60} src={String(companyLogo)} alt="company-logo" className='contain' />
						</>
					)}
						{companyDescription && (
							<>
								<div className="key-information-title mt-25 mb-7">
									Company description
								</div>
								<div className="userValue mb-0">
									{companyDescription}
								</div>
							</>
						)}
					</div>

				</>
			}

			{(guest?.videos && guest?.videos.length > 0 && guest?.videos[0] !== "" || guest?.images && guest?.images.length > 0) && (
				<>
					<BaseDivider />
					<div className="key-information" style={{ marginTop: '10px' }}>
						Gallery
					</div>
					<Grid item md={12}>
						<MediaSection
							className={classes.media}
							videos={guest?.videos || []}
							images={guest?.images || []}
							comeFrom="overview"
						/>
					</Grid>
				</>
			)}
			<BaseDivider />
		</div>
	);
};
